import { CAlert, CButton, CButtonGroup, CTooltip } from '@coreui/react'
import { faGithub } from '@fortawesome/free-brands-svg-icons'
import {
	faCog,
	faExclamationTriangle,
	faExternalLink,
	faPlug,
	faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useRef, useState } from 'react'
import { ModuleInstanceType } from '@companion-app/shared/Model/Instance.js'
import { InlineHelpCustom } from '~/Components/InlineHelp.js'
import { NonIdealState } from '~/Components/NonIdealState.js'
import { SearchBox } from '~/Components/SearchBox.js'
import { useTableVisibilityHelper } from '~/Components/TableVisibility.js'
import { WindowLinkOpen } from '~/Helpers/Window.js'
import { filterProducts, useAllModuleProducts, type FuzzyProduct } from '~/Hooks/useFilteredProducts.js'
import { CloseButton, ContextHelpButton, type ContextHelpButtonProps } from '~/Layout/PanelIcons.js'
import { LastUpdatedTimestamp } from '~/Modules/LastUpdatedTimestamp.js'
import { RefreshModulesList } from '~/Modules/RefreshModulesList.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { AddInstanceModal, type AddInstanceModalRef } from './AddInstanceModal.js'
import type { AddInstanceService } from './AddInstanceService.js'

interface AddInstancePanelProps {
	service: AddInstanceService

	title: string
	helpAction: ContextHelpButtonProps['action']
	description: (modulesCount: number) => React.ReactNode
	isSubpanel?: boolean
}

export const AddInstancePanel = observer(function AddInstancePanel({
	service,
	title,
	helpAction,
	description,
	isSubpanel,
}: AddInstancePanelProps) {
	const { modules } = useContext(RootAppStoreContext)

	const [filter, setFilter] = useState('')

	const addRef = useRef<AddInstanceModalRef>(null)
	const addInstance = useCallback((moduleInfo: FuzzyProduct) => {
		addRef.current?.show(moduleInfo)
	}, [])

	const typeFilter = useTableVisibilityHelper(`${service.moduleType}-add-type-filter`, {
		available: true,
	})

	// The number of modules (see next comment)
	const storeModulesOfTypeCount = modules.countStoreModulesOfType(service.moduleType)

	// A module can support several devices: useAllModuleProducts returns the list of devices, so some modules are represented by several entries here.
	const allProducts = useAllModuleProducts(service.moduleType)
	const typeProducts = allProducts.filter(
		(p) => storeModulesOfTypeCount === 0 || !!p.installedInfo || typeFilter.visibility.available
	)

	let candidates: JSX.Element[] = []
	try {
		const searchResults = filterProducts(typeProducts, filter, false)

		const candidatesObj: Record<string, JSX.Element> = {}
		for (const moduleInfo of searchResults) {
			candidatesObj[moduleInfo.name] = (
				<AddInstanceEntry key={moduleInfo.name} moduleInfo={moduleInfo} addInstance={addInstance} />
			)
		}

		if (!filter) {
			candidates = Object.entries(candidatesObj)
				.sort((a, b) => {
					const aName = a[0].toLocaleLowerCase()
					const bName = b[0].toLocaleLowerCase()
					if (aName < bName) return -1
					if (aName > bName) return 1
					return 0
				})
				.map((c) => c[1])
		} else {
			candidates = Object.entries(candidatesObj).map((c) => c[1])
		}
	} catch (e) {
		console.error('Failed to compile candidates list:', e)

		candidates = []
		candidates.push(
			<CAlert color="warning" role="alert">
				Failed to build list of modules:
				<br />
				{e?.toString()}
			</CAlert>
		)
	}

	const includeStoreModules = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			typeFilter.toggleVisibility('available', true)
		},
		[typeFilter]
	)

	return (
		<>
			<div className="secondary-panel-simple-header">
				<h4 className="panel-title">{title}</h4>
				<div className="header-buttons">
					<ContextHelpButton action={helpAction} />
					<CloseButton closeFn={service.closeAddInstance} visibilityClass={isSubpanel ? '' : 'd-xl-none'} />
				</div>
			</div>

			<div className="secondary-panel-simple-body">
				<AddInstanceModal ref={addRef} service={service} openConfigureInstance={service.openConfigureInstance} />
				<div style={{ clear: 'both' }} className="row-heading">
					<div className="add-connection-intro-section mb-3">
						{storeModulesOfTypeCount > 0 ? (
							<div className="intro-grid">
								{description(storeModulesOfTypeCount)}
								<div className="intro-filter">
									<CButtonGroup role="group" aria-label="Module visibility filter">
										<CButton
											size="sm"
											color={!typeFilter.visibility.available ? 'info' : 'outline-info'}
											onClick={() => typeFilter.toggleVisibility('available')}
											disabled={!typeFilter.visibility.available}
										>
											Installed Only
										</CButton>
										<CButton
											size="sm"
											color={typeFilter.visibility.available ? 'info' : 'outline-info'}
											onClick={() => typeFilter.toggleVisibility('available')}
											disabled={typeFilter.visibility.available}
										>
											All Available
										</CButton>
									</CButtonGroup>
								</div>
							</div>
						) : (
							<CAlert color="info" className="mb-0">
								<div className="d-flex align-items-center gap-2">
									<FontAwesomeIcon icon={faPlug} className="text-info" />
									{description(0)}
								</div>
							</CAlert>
						)}
					</div>

					<div>
						<div className="refresh-and-last-updated mb-3">
							<RefreshModulesList btnSize="sm" />
							<LastUpdatedTimestamp timestamp={modules.storeUpdateInfo.lastUpdated} />
						</div>

						<SearchBox filter={filter} setFilter={setFilter} />
					</div>
				</div>
				<div id="connection_add_search_results">
					{candidates}

					{candidates.length === 0 && allProducts.length > 0 && (
						<NonIdealState icon={faPlug}>
							No modules match your search.
							<br />
							{!typeFilter.visibility.available && (
								<a href="#" onClick={includeStoreModules}>
									Click here to include modules from the store
								</a>
							)}
						</NonIdealState>
					)}

					{candidates.length === 0 && allProducts.length === 0 && (
						<NonIdealState icon={faPlug}>
							No modules are installed.
							<br />
							Make sure you have an active internet connection, or load a module bundle into the{' '}
							<Link to="/modules">Modules tab</Link>
						</NonIdealState>
					)}
				</div>
			</div>
		</>
	)
})

interface AddInstanceEntryProps {
	moduleInfo: FuzzyProduct
	addInstance: (module: FuzzyProduct) => void
}

const AddInstanceEntry = observer(function AddInstanceEntry({ moduleInfo, addInstance }: AddInstanceEntryProps) {
	const { helpViewer, surfaceInstances } = useContext(RootAppStoreContext)

	const addInstanceClick = useCallback(() => addInstance(moduleInfo), [addInstance, moduleInfo])
	const showHelpForVersion =
		moduleInfo.installedInfo?.devVersion ??
		moduleInfo.installedInfo?.stableVersion ??
		moduleInfo.installedInfo?.betaVersion ??
		moduleInfo.installedInfo?.builtinVersion ??
		moduleInfo.installedInfo?.installedVersions?.[0] ??
		(moduleInfo.storeInfo
			? { helpPath: moduleInfo.storeInfo.helpUrl, versionId: '', allowMultipleInstances: true }
			: undefined)

	const showHelpClick = useCallback(
		() =>
			showHelpForVersion?.helpPath &&
			helpViewer.current?.showFromUrl(
				moduleInfo.moduleType,
				moduleInfo.moduleId,
				showHelpForVersion.versionId,
				showHelpForVersion.helpPath
			),
		[
			helpViewer,
			moduleInfo.moduleType,
			moduleInfo.moduleId,
			showHelpForVersion?.helpPath,
			showHelpForVersion?.versionId,
		]
	)

	const alreadyAddedCount =
		moduleInfo.moduleType === ModuleInstanceType.Surface
			? surfaceInstances.getAllOfModuleId(moduleInfo.moduleId).length
			: 0
	// If it is installed, we know if it supports multiple instances.
	// If it isn't installed, count will be 0, so it doesn't matter
	const isLimitReached = alreadyAddedCount > 0 && !(showHelpForVersion?.allowMultipleInstances ?? false)

	return (
		<div className="flex">
			{isLimitReached ? (
				<CTooltip content="This module is limited to one instance">
					<span>
						<CButton color="primary" disabled>
							Add
						</CButton>
					</span>
				</CTooltip>
			) : (
				<CButton color="primary" onClick={addInstanceClick}>
					Add
				</CButton>
			)}
			&nbsp;
			{moduleInfo.installedInfo?.stableVersion?.isLegacy && (
				<InlineHelpCustom help="This module has not been updated for Companion 3.0, and may not work fully.">
					<FontAwesomeIcon
						icon={faExclamationTriangle}
						color="#ff6600"
						size={'xl'}
						aria-label="This module has not been updated for Companion 3.0, and may not work fully."
					/>
					&nbsp;
				</InlineHelpCustom>
			)}
			<div className="grow" style={{ alignContent: 'center' }}>
				{moduleInfo.name}
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
				<Link
					to={`/modules/$moduleType/$moduleId`}
					params={{ moduleType: moduleInfo.moduleType, moduleId: moduleInfo.moduleId }}
					className="text-decoration-none"
				>
					<div
						className="m-0"
						style={{ display: 'inline-block', color: 'var(--cui-body-color)' }}
						title={'Manage module'}
					>
						<FontAwesomeIcon icon={faCog} />
					</div>
				</Link>
				{!!moduleInfo.storeInfo && (
					<WindowLinkOpen className="m-0" title="Open Store Page" href={moduleInfo.storeInfo.storeUrl}>
						<FontAwesomeIcon icon={faExternalLink} />
					</WindowLinkOpen>
				)}
				{!!moduleInfo.storeInfo?.githubUrl && (
					<WindowLinkOpen className="m-0" title="Open GitHub Page" href={moduleInfo.storeInfo.githubUrl}>
						<FontAwesomeIcon icon={faGithub} />
					</WindowLinkOpen>
				)}
				{showHelpForVersion?.helpPath && (
					<div className="m-0" style={{ cursor: 'pointer' }} onClick={showHelpClick}>
						<FontAwesomeIcon icon={faQuestionCircle} />
					</div>
				)}
			</div>
		</div>
	)
})
